import React, { useRef, useEffect, useState } from 'react';

interface EmailContentFrameProps {
	content: string;
	isDarkMode?: boolean;
	preserveOriginalStyles?: boolean; // New prop to control whether to preserve original email styles
}

/**
 * Component to render email content in an isolated iframe with controlled styling
 */
export const EmailContentFrame: React.FC<EmailContentFrameProps> = ({ 
	content, 
	isDarkMode = false,
	preserveOriginalStyles = true // Default to preserving original styling
}) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [height, setHeight] = useState<number>(300);
	const [loaded, setLoaded] = useState(false);

	// Create sanitized HTML content
	const sanitizeHTML = (html: string): string => {
		if (!html) return '';
		
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;
		
		// Remove potentially dangerous elements
		const dangerousElements = tempDiv.querySelectorAll('script, iframe, object, embed, form, input, button');
		dangerousElements.forEach(el => el.remove());
		
		// Remove dangerous attributes
		const allElements = tempDiv.querySelectorAll('*');
		allElements.forEach(el => {
			const dangerousAttrs = ['onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout', 'onfocus', 'onblur'];
			dangerousAttrs.forEach(attr => {
				if (el.hasAttribute(attr)) {
					el.removeAttribute(attr);
				}
			});
			
			// Remove javascript: links
			if (el.hasAttribute('href') && el.getAttribute('href')?.startsWith('javascript:')) {
				el.removeAttribute('href');
			}
		});
		
		return tempDiv.innerHTML;
	};

	// Generate the full HTML document for the iframe
	const generateIframeContent = (): string => {
		const sanitizedContent = sanitizeHTML(content);
		
		// Check if content is likely a dark-mode email that should be preserved
		const isSupabaseEmail = content.includes('obuldanrptloktxcffvn.supabase.co') || 
								content.includes('supabase.com/dashboard');
		const hasDarkModeMediaQuery = content.includes('@media (prefers-color-scheme: dark)');
		
		// Determine whether to use our styling or preserve original
		const shouldApplyCustomStyles = !preserveOriginalStyles || 
									   (!isSupabaseEmail && !hasDarkModeMediaQuery);
		
		// Define CSS variables based on dark/light mode - only used if we apply custom styles
		const bgColor = isDarkMode ? '#171717' : '#ffffff';
		const textColor = isDarkMode ? '#ffffff' : '#171717';
		const borderColor = isDarkMode ? '#4b5563' : '#e5e7eb';
		const linkColor = isDarkMode ? '#3b82f6' : '#2563eb';
		
		// Create complete HTML document with responsive styling
		return `
			<!DOCTYPE html>
			<html class="${isDarkMode ? 'dark-mode' : 'light-mode'}" data-force-theme="${isDarkMode ? 'dark' : 'light'}">
			<head>
				<meta charset="utf-8">
				<meta name="viewport" content="width=device-width, initial-scale=1">
				${isDarkMode ? '<meta name="color-scheme" content="dark">' : '<meta name="color-scheme" content="light">'}
				<style>
					:root {
						color-scheme: ${isDarkMode ? 'dark' : 'light'};
					}
					
					body {
						font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
						line-height: 1.6;
						margin: 0;
						padding: 0;
						${shouldApplyCustomStyles ? `
						color: ${textColor};
						background-color: ${bgColor};
						` : ''}
					}
					
					* {
						box-sizing: border-box;
					}
					
					img {
						max-width: 100%;
						height: auto;
						${isDarkMode && shouldApplyCustomStyles ? 'opacity: 0.9;' : ''}
					}
					
					${shouldApplyCustomStyles ? `
					a {
						color: ${linkColor};
						text-decoration: underline;
					}
					
					p, ul, ol {
						margin: 0.5em 0;
					}
					
					table {
						border-collapse: collapse;
						width: 100%;
						margin: 0.5em 0;
					}
					
					th, td {
						border: 1px solid ${borderColor};
						padding: 0.25em 0.5em;
						text-align: left;
					}
					
					blockquote {
						border-left: 4px solid ${borderColor};
						padding-left: 1em;
						margin: 0.5em 0;
						font-style: italic;
					}
					
					h1, h2, h3, h4, h5, h6 {
						margin: 0.75em 0 0.25em 0;
						font-weight: 600;
						line-height: 1.2;
					}` : ''}
					
					${shouldApplyCustomStyles && isDarkMode ? `
					/* Force styles in dark mode only if we're applying custom styles */
					[style*="background"], [bgcolor], [style*="background-color"] {
						background-color: ${bgColor} !important;
					}
					
					[style*="color:"] {
						color: ${textColor} !important;
					}` : ''}
				</style>
			</head>
			<body>
				${sanitizedContent}
				<script>
					// Send height to parent window for responsive sizing
					function updateHeight() {
						const height = document.body.scrollHeight;
						window.parent.postMessage({ type: 'resize', height: height }, '*');
					}
					
					// Update on load and when images load
					window.addEventListener('load', updateHeight);
					document.addEventListener('DOMContentLoaded', updateHeight);
					
					// Update when images load
					const images = document.querySelectorAll('img');
					images.forEach(img => {
						img.addEventListener('load', updateHeight);
					});
					
					// Check periodically for dynamic content changes
					setInterval(updateHeight, 500);
					
					${shouldApplyCustomStyles && isDarkMode ? `
					// Apply forced styles to all elements - only in dark mode and if we should apply custom styles
					function applyForcedStyles() {
						const elements = document.querySelectorAll('*');
						elements.forEach(el => {
							if (el.tagName !== 'HTML' && el.tagName !== 'HEAD' && el.tagName !== 'BODY' && el.tagName !== 'STYLE' && el.tagName !== 'SCRIPT') {
								// Force background color
								if (el.style.backgroundColor) {
									el.style.backgroundColor = '${bgColor}';
								}
								// Force text color
								if (el.style.color) {
									el.style.color = '${textColor}';
								}
							}
						});
					}
					
					// Apply initial styles and periodically check
					applyForcedStyles();
					setInterval(applyForcedStyles, 1000);` : ''}
				</script>
			</body>
			</html>
		`;
	};

	// Handle iframe load
	const handleIframeLoad = () => {
		setLoaded(true);
		
		// Access the iframe document to get its height
		if (iframeRef.current) {
			try {
				const frameHeight = iframeRef.current.contentWindow?.document.body.scrollHeight || 300;
				setHeight(frameHeight);
			} catch (e) {
				console.error('Error accessing iframe content:', e);
			}
		}
	};

	useEffect(() => {
		// Handle messages from iframe for dynamic resizing
		const handleMessage = (event: MessageEvent) => {
			if (event.data && event.data.type === 'resize') {
				setHeight(event.data.height);
			}
		};

		window.addEventListener('message', handleMessage);
		
		// Cleanup
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	// When content changes, reset iframe if already loaded
	useEffect(() => {
		if (loaded && iframeRef.current) {
			// Write new content to iframe
			const doc = iframeRef.current.contentWindow?.document;
			if (doc) {
				doc.open();
				doc.write(generateIframeContent());
				doc.close();
				
				// Update height after content change
				setTimeout(() => {
					try {
						const frameHeight = doc.body.scrollHeight || 300;
						setHeight(frameHeight);
					} catch (e) {
						console.error('Error accessing iframe content after update:', e);
					}
				}, 100);
			}
		}
	}, [content, isDarkMode, loaded]);

	return (
		<iframe
			ref={iframeRef}
			srcDoc={generateIframeContent()}
			title="Email content"
			style={{
				width: '100%',
				height: `${height}px`, 
				border: 'none',
				overflow: 'hidden',
				backgroundColor: 'transparent'
			}}
			onLoad={handleIframeLoad}
			sandbox="allow-same-origin"
		/>
	);
};
